import { useAuth } from 'components/AuthProvider'
import {
  DataPoint,
  GraphPeriodType,
  buildDataHistoricalHoldingBalances
} from 'components/PerformanceGraph'
import { useWalletPerformanceHistory } from 'hooks/useWallet'
import {
  useProductPerformance,
  usePerformanceSummaries
} from 'hooks/useInventory'
import useVault from 'hooks/useVault/useVault'
import { useMemo } from 'react'

interface Props {
  productId?: number
  // The vault this graph is scoped to. The insight graph inside a kids vault
  // MUST pass it [BUG-019] — without it the backend sums allocations across
  // every vault and a $10 kids vault plots the whole family's performance.
  vaultId?: number
  graphPeriod: GraphPeriodType
}

// The backend series deliberately ends YESTERDAY (Rails parity) and the UI
// appends a synthetic point for today. That point used to come from
// products/{productId ?? 0}/performance — product 0 does not exist, so the
// vault graph (no productId) plotted $0.00 for today, and the insight graph
// used an unscoped value [BUG-013, BUG-019, "trade not in the graph"].
export function usePerformanceGraph({
  productId,
  vaultId,
  graphPeriod
}: Props) {
  const { activatedVaultId } = useVault()

  const { user } = useAuth()

  const defaultVaultId = useMemo(() => {
    return user?.vaults?.[0]?.id
  }, [user?.vaults])

  // Explicit prop first (a kids-vault insight graph), then the activated
  // vault, then the first vault — the same resolution the history query used.
  const effectiveVaultId =
    vaultId ??
    (activatedVaultId
      ? Number(activatedVaultId)
      : defaultVaultId
      ? Number(defaultVaultId)
      : undefined)

  const vaultIds = effectiveVaultId ? [effectiveVaultId] : undefined

  const { data: historicalPerformance, isLoading: historyLoading } =
    useWalletPerformanceHistory(
      productId ? [productId] : undefined,
      vaultIds,
      graphPeriod
    )

  // Today's value, from the SAME vault scope as the history:
  //  - product graph: that product's current value in this vault
  //  - vault graph: every product's current value in this vault, summed
  const { data: productToday, isLoading: productTodayLoading } =
    useProductPerformance(productId ?? 0, effectiveVaultId, {
      enabled: Boolean(productId)
    })
  const { data: summaries, isLoading: summariesLoading } =
    usePerformanceSummaries(effectiveVaultId, { enabled: !productId })

  const totalProductPerformanceToday = productId
    ? Number(productToday?.currentValue ?? 0)
    : (summaries ?? []).reduce(
        (sum, summary) => sum + Number(summary.currentValue ?? 0),
        0
      )

  const loading =
    historyLoading || (productId ? productTodayLoading : summariesLoading)

  const builtData: DataPoint[] = useMemo(() => {
    return buildDataHistoricalHoldingBalances({
      data: historicalPerformance,
      productPriceToday: totalProductPerformanceToday,
      graphPeriod
    })
  }, [graphPeriod, historicalPerformance, totalProductPerformanceToday])

  return {
    builtData,
    loading
  }
}

export default usePerformanceGraph
